// src/pages/channels/ChannelDetails.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Settings, Trash2, Edit, Save, X, 
  CheckCircle, XCircle, Clock, AlertTriangle,
  Phone, MessageCircle, Instagram, ExternalLink,
  Copy, Eye, EyeOff, Info, Loader2,
  Users, MessageSquare, Calendar, TrendingUp,
  Wifi, Shield, Key
} from 'lucide-react';
import { channelService, agentService } from '../../services/PlatformService';
import WhatsAppQRConfiguration from '../../components/channels/WhatsAppQRConfiguration';

const ChannelDetails = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  
  const [channel, setChannel] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [copied, setCopied] = useState('');
  
  const [editData, setEditData] = useState({
    name: '',
    agent_id: '',
    config: {}
  });

  useEffect(() => {
    loadData();
  }, [channelId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [channelRes, agentsRes] = await Promise.all([
        channelService.getChannel(channelId),
        agentService.getUserAgents()
      ]);
      
      setChannel(channelRes.data);
      setAgents(agentsRes.data || []);
      
      // Initialize edit data
      setEditData({
        name: channelRes.data.name,
        agent_id: channelRes.data.agent_id,
        config: channelRes.data.config || {}
      });
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Erro ao carregar canal');
      navigate('/channels');
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (type) => {
    const icons = {
      'whatsapp_business': <img 
            src="https://img.icons8.com/?size=512&id=16713&format=png" 
            alt="WhatsApp" 
            title="WhatsApp" 
            className="w-6 h-6"
          />,
      'whatsapp_qr': <img 
            src="https://img.icons8.com/?size=512&id=16713&format=png" 
            alt="WhatsApp" 
            title="WhatsApp" 
            className="w-6 h-6"
          />,
      'instagram_direct': <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Instagram_logo_2016.svg/2000px-Instagram_logo_2016.svg.png"} 
            alt="instagram_direct" 
            className="w-5 h-5"
          />,
      'messenger': <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/b/be/Facebook_Messenger_logo_2020.svg"} 
            alt="messenger" 
            className="w-5 h-5"
          />
    };
    return icons[type] || <MessageSquare className="w-8 h-8 text-gray-600" />;
  };

  const getChannelTypeName = (type) => {
    const names = {
      'whatsapp_business': 'WhatsApp Business API',
      'whatsapp_qr': 'WhatsApp QR Code',
      'instagram_direct': 'Instagram Direct',
      'messenger': 'Facebook Messenger'
    };
    return names[type] || type;
  };

  const getStatusConfig = (status) => {
    const config = {
      'active': { 
        icon: <CheckCircle className="w-6 h-6" />, 
        color: 'bg-green-100 text-green-700 border-green-200', 
        label: 'Ativo',
        bgIcon: 'bg-green-500'
      },
      'pending': { 
        icon: <Clock className="w-6 h-6" />, 
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200', 
        label: 'Pendente',
        bgIcon: 'bg-yellow-500'
      },
      'inactive': { 
        icon: <XCircle className="w-6 h-6" />, 
        color: 'bg-red-100 text-red-700 border-red-200', 
        label: 'Inativo',
        bgIcon: 'bg-red-500'
      },
      'error': { 
        icon: <AlertTriangle className="w-6 h-6" />, 
        color: 'bg-red-100 text-red-700 border-red-200', 
        label: 'Erro',
        bgIcon: 'bg-red-500'
      }
    };
    
    return config[status] || config.inactive;
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await channelService.updateChannel(channelId, editData);
      await loadData();
      setIsEditing(false);
      alert('Canal atualizado com sucesso!');
    } catch (error) {
      console.error('Error saving channel:', error);
      alert('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir este canal? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      await channelService.deleteChannel(channelId);
      navigate('/channels');
    } catch (error) {
      console.error('Error deleting channel:', error);
      alert('Erro ao excluir canal');
    }
  };

  const handleContinueSetup = () => {
    navigate(`/channels/${channelId}/configure`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-blue-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando detalhes do canal...</p>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(channel.status);
  const selectedAgent = agents.find(a => a.id === channel.agent_id);
  const isConfigComplete = channel.status === 'active' || (channel.config && Object.keys(channel.config).length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/channels')}
                className="p-2 hover:bg-gray-100 rounded-xl transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                {getChannelIcon(channel.type)}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{channel.name}</h1>
                  <p className="text-sm text-gray-600">{getChannelTypeName(channel.type)}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {channel.status === 'pending' && isConfigComplete && (
                <button
                  onClick={handleContinueSetup}
                  className="px-4 py-2 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-all flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Continuar Configuração
                </button>
              )}
              
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditData({
                        name: channel.name,
                        agent_id: channel.agent_id,
                        config: channel.config || {}
                      });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:bg-gray-300 transition-all flex items-center gap-2"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`border-b-2 ${statusConfig.color}`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {statusConfig.icon}
              <span className="font-semibold">Status: {statusConfig.label}</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span>Criado em: {new Date(channel.created_at).toLocaleDateString('pt-BR')}</span>
              {channel.updated_at && (
                <span>Atualizado: {new Date(channel.updated_at).toLocaleDateString('pt-BR')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {['details', 'config', 'stats', 'danger'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium transition-all relative ${
                  activeTab === tab
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'details' && 'Detalhes'}
                {tab === 'config' && 'Configuração'}
                {tab === 'stats' && 'Estatísticas'}
                {tab === 'danger' && 'Zona de Perigo'}
                
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'details' && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Informações Básicas */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Info className="w-6 h-6 text-green-600" />
                Informações Básicas
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Canal
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                    />
                  ) : (
                    <p className="text-gray-900">{channel.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Canal
                  </label>
                  <div className="flex items-center gap-2">
                    {getChannelIcon(channel.type)}
                    <span className="text-gray-900">{getChannelTypeName(channel.type)}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID do Canal
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono">
                      {channel.id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(channel.id, 'channel_id')}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                    >
                      {copied === 'channel_id' ? 
                        <CheckCircle className="w-4 h-4 text-green-600" /> : 
                        <Copy className="w-4 h-4 text-gray-500" />
                      }
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Funcionário Associado
                  </label>
                  {isEditing ? (
                    <select
                      value={editData.agent_id}
                      onChange={(e) => setEditData(prev => ({ ...prev, agent_id: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                    >
                      <option value="">Selecione um funcionário</option>
                      {agents.map(agent => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Link
                      to={`/agent-manager/${channel.agent_id}`}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all"
                    >
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        {selectedAgent?.widget_settings?.logo_url ? (
                          <img 
                            src={selectedAgent.widget_settings.logo_url} 
                            alt={selectedAgent.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <Users className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {selectedAgent?.name || 'Funcionário não encontrado'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedAgent?.description || 'Sem descrição'}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Status e Webhook */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Wifi className="w-6 h-6 text-green-600" />
                Conexão e Webhook
              </h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Status da Conexão</span>
                    <div className={`w-3 h-3 rounded-full ${statusConfig.bgIcon}`} />
                  </div>
                  <p className="text-sm text-gray-600">
                    {channel.status === 'active' && 'Canal conectado e funcionando'}
                    {channel.status === 'pending' && 'Aguardando configuração completa'}
                    {channel.status === 'inactive' && 'Canal desconectado'}
                    {channel.status === 'error' && 'Erro na conexão do canal'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL do Webhook
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={channel.webhook_url || 'Webhook não configurado'}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(channel.webhook_url || '', 'webhook')}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                      disabled={!channel.webhook_url}
                    >
                      {copied === 'webhook' ? 
                        <CheckCircle className="w-4 h-4 text-green-600" /> : 
                        <Copy className="w-4 h-4 text-gray-500" />
                      }
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Verificado
                  </label>
                  <div className="flex items-center gap-2">
                    {channel.webhook_verified ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-700">Verificado</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-red-600" />
                        <span className="text-red-700">Não verificado</span>
                      </>
                    )}
                  </div>
                </div>

                {channel.webhook_verified_at && (
                  <p className="text-sm text-gray-600">
                    Verificado em: {new Date(channel.webhook_verified_at).toLocaleString('pt-BR')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Key className="w-6 h-6 text-green-600" />
              Configuração do {getChannelTypeName(channel.type)}
            </h2>

            {channel.type === 'whatsapp_business' && (
              <div className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ID da Conta Empresarial
                        </label>
                        <input
                          type="text"
                          value={editData.config.businessId || ''}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            config: { ...prev.config, businessId: e.target.value }
                          }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ID da Conta WhatsApp
                        </label>
                        <input
                          type="text"
                          value={editData.config.accountId || ''}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            config: { ...prev.config, accountId: e.target.value }
                          }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número do WhatsApp
                        </label>
                        <input
                          type="text"
                          value={editData.config.chatbotNumber || ''}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            config: { ...prev.config, chatbotNumber: e.target.value }
                          }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ID do Número
                        </label>
                        <input
                          type="text"
                          value={editData.config.phoneNumberId || ''}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            config: { ...prev.config, phoneNumberId: e.target.value }
                          }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Token de Acesso
                      </label>
                      <div className="relative">
                        <input
                          type={showToken ? "text" : "password"}
                          value={editData.config.accessToken || ''}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            config: { ...prev.config, accessToken: e.target.value }
                          }))}
                          className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowToken(!showToken)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {channel.config && Object.keys(channel.config).length > 0 ? (
                      <div className="space-y-3">
                        {channel.config.businessId && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">ID da Conta Empresarial</span>
                            <span className="font-mono">{channel.config.businessId}</span>
                          </div>
                        )}
                        {channel.config.accountId && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">ID da Conta WhatsApp</span>
                            <span className="font-mono">{channel.config.accountId}</span>
                          </div>
                        )}
                        {channel.config.chatbotNumber && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">Número do WhatsApp</span>
                            <span className="font-mono">{channel.config.chatbotNumber}</span>
                          </div>
                        )}
                        {channel.config.phoneNumberId && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">ID do Número</span>
                            <span className="font-mono">{channel.config.phoneNumberId}</span>
                          </div>
                        )}
                        {channel.config.accessToken && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-gray-600">Token de Acesso</span>
                            <span className="font-mono">••••••••••</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 mb-4">Nenhuma configuração encontrada</p>
                        <button
                          onClick={handleContinueSetup}
                          className="text-green-600 hover:text-green-700 font-medium"
                        >
                          Configurar agora →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {channel.type === 'whatsapp_qr' && (
              <WhatsAppQRConfiguration channel={channel} isEditing={isEditing} editData={editData} setEditData={setEditData} />
            )}

            {/* ========== INSTAGRAM DIRECT ========== */}
    {channel.type === 'instagram_direct' && (
      <div className="space-y-4">
        {isEditing ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instagram Account ID
              </label>
              <input
                type="text"
                value={editData.config.instagramAccountId || ''}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  config: { ...prev.config, instagramAccountId: e.target.value }
                }))}
                placeholder="123456789012345"
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                ID numérico da sua conta profissional do Instagram
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Token
              </label>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  value={editData.config.accessToken || ''}
                  onChange={(e) => setEditData(prev => ({
                    ...prev,
                    config: { ...prev.config, accessToken: e.target.value }
                  }))}
                  placeholder="EAAD..."
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Token de acesso da Página vinculada à conta do Instagram
              </p>
            </div>

            {/* Info adicional */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Como obter essas credenciais:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700">
                    <li>Acesse o Meta App Dashboard</li>
                    <li>Vá em Instagram → Settings</li>
                    <li>Selecione sua conta profissional</li>
                    <li>Gere o Page Access Token</li>
                    <li>Copie o Instagram Account ID</li>
                  </ol>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {channel.config && Object.keys(channel.config).length > 0 ? (
              <div className="space-y-3">
                {channel.config.instagramAccountId && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Instagram Account ID</span>
                    <span className="font-mono text-sm">{channel.config.instagramAccountId}</span>
                  </div>
                )}
                {channel.config.accessToken && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Access Token</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">
                        {showToken 
                          ? channel.config.accessToken 
                          : `${channel.config.accessToken.substring(0, 8)}...${channel.config.accessToken.substring(channel.config.accessToken.length - 4)}`
                        }
                      </span>
                      <button
                        onClick={() => setShowToken(!showToken)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">Nenhuma configuração encontrada</p>
                <button
                  onClick={handleContinueSetup}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Configurar agora →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    )}

    {/* ========== FACEBOOK MESSENGER ========== */}
    {channel.type === 'messenger' && (
      <div className="space-y-4">
        {isEditing ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page ID
              </label>
              <input
                type="text"
                value={editData.config.page_id || ''}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  config: { ...prev.config, page_id: e.target.value }
                }))}
                placeholder="123456789012345"
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                ID numérico da sua Página do Facebook
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page Access Token
              </label>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  value={editData.config.page_access_token || ''}
                  onChange={(e) => setEditData(prev => ({
                    ...prev,
                    config: { ...prev.config, page_access_token: e.target.value }
                  }))}
                  placeholder="EAAD..."
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Token de acesso da Página do Facebook
              </p>
            </div>

            {/* Info adicional */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Como obter essas credenciais:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700">
                    <li>Acesse o Meta App Dashboard</li>
                    <li>Crie um App e adicione o produto "Messenger"</li>
                    <li>Vá em Messenger → Settings</li>
                    <li>Em "Access Tokens", selecione sua Página</li>
                    <li>Clique em "Generate Token"</li>
                    <li>Copie o Page Access Token e o Page ID</li>
                  </ol>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {channel.config && Object.keys(channel.config).length > 0 ? (
              <div className="space-y-3">
                {channel.config.page_id && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Page ID</span>
                    <span className="font-mono text-sm">{channel.config.page_id}</span>
                  </div>
                )}
                {channel.config.page_access_token && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Page Access Token</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">
                        {showToken 
                          ? channel.config.page_access_token 
                          : `${channel.config.page_access_token.substring(0, 8)}...${channel.config.page_access_token.substring(channel.config.page_access_token.length - 4)}`
                        }
                      </span>
                      <button
                        onClick={() => setShowToken(!showToken)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">Nenhuma configuração encontrada</p>
                <button
                  onClick={handleContinueSetup}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Configurar agora →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Estatísticas Gerais */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
                Estatísticas Gerais
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <MessageSquare className="w-5 h-5 text-gray-500" />
                    <span className="text-2xl font-bold text-gray-900">
                      {channel.stats?.total_messages || 0}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Total de Mensagens</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <span className="text-2xl font-bold text-gray-900">
                      {channel.stats?.total_chats || 0}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Conversas</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-5 h-5 text-gray-500" />
                    <span className="text-2xl font-bold text-gray-900">
                      {channel.stats?.messages_today || 0}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Mensagens Hoje</p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <span className="text-xl font-bold text-gray-900">
                      {channel.stats?.avg_response_time || 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Tempo Médio Resposta</p>
                </div>
              </div>
            </div>

            {/* Atividade Recente */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-green-600" />
                Atividade Recente
              </h2>
              
              {channel.stats?.last_message_at ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm font-medium text-blue-900 mb-1">Última Mensagem</p>
                    <p className="text-blue-700">
                      {new Date(channel.stats.last_message_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  
                  {channel.stats?.last_error_at && (
                    <div className="p-4 bg-red-50 rounded-xl">
                      <p className="text-sm font-medium text-red-900 mb-1">Último Erro</p>
                      <p className="text-red-700">
                        {new Date(channel.stats.last_error_at).toLocaleString('pt-BR')}
                      </p>
                      {channel.stats?.last_error_message && (
                        <p className="text-sm text-red-600 mt-1">
                          {channel.stats.last_error_message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">Nenhuma atividade registrada</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'danger' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-red-600 mb-6 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Zona de Perigo
            </h2>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <p className="text-red-800 mb-4">
                <strong>Atenção:</strong> As ações abaixo são permanentes e não podem ser desfeitas.
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-2">Excluir Canal</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Ao excluir este canal, todas as configurações serão perdidas permanentemente.
                  As conversas existentes não serão afetadas.
                </p>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir Canal Permanentemente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelDetails;